import '@ag-grid-community/styles/ag-grid.css';
import '@ag-grid-community/styles/ag-theme-material.css';
import { AgGridReact } from '@ag-grid-community/react';
import { Grid } from '@mui/material';
import { useAgGrid } from '@tassat/ag-grid';
import { ActionTypes, doesWorkflowRejectCodeExist } from '@tassat/common-definitions';
import { ApiConnectionFirmMapping } from '@tassat/fred-extended-definitions';
import {
    ConfirmationWorkflowAction,
    ConfirmationWorkflowConsumer,
    ConfirmationWorkflowContext,
    ConfirmationWorkflowReducer,
    DEFAULT_INITIAL_STATE
} from '@tassat/workflow';
import React from 'react';

import { highlightLockedRow } from 'Lib/agGridHelper';
import Worker from 'Lib/worker/PrimusWorker';
import { useAppSelector } from 'Reducers/store/hooks';
import { getAPIConnectionFirmsFromConnectionID } from 'Selectors/apiConnectionFirms';
import { ActionButtonsCellRenderer } from 'SharedComponents/cellRenderComponent/actionButtonsCellRenderer';

import { ApiConnectionFirmsColumnDefs } from './apiFirmsGridConfig';

import type { IAPIFirmsAgGridProps } from '.';

import './apiFirmsViewStyles.css';

export function APIConnectionFirmsAgGrid(props: IAPIFirmsAgGridProps) {
    const { apiConnectionID } = props;
    const firmsFromApiConnection = useAppSelector((state) => getAPIConnectionFirmsFromConnectionID(state, '', apiConnectionID));
    const [ ,onGridReady ] = useAgGrid();

    const [ confirmationWorkflowState, dispatchConfirmationWorkflowAction ] =
    React.useReducer(ConfirmationWorkflowReducer, DEFAULT_INITIAL_STATE);

    const confirmationContext = React.useMemo(() => {
        return {
            confirmationWorkflowState,
            dispatchConfirmationWorkflowAction
        };
    }, [ confirmationWorkflowState, dispatchConfirmationWorkflowAction ]);

    const lockApiFirmValidation = (apiConnectionFirm: ApiConnectionFirmMapping): boolean => {
        const apiFirm = apiConnectionFirm.apiFirm;
        const firm = apiConnectionFirm.firm;
        const apiConnection = apiConnectionFirm.apiConnection;

        const validationSuccessfulAction: ConfirmationWorkflowAction = {
            type: 'VALIDATION_SUCCESS',
            workflowConfiguration: {
                selectedItem: apiConnectionFirm,
                actionType: apiFirm.IsLocked ? ActionTypes.unlock : ActionTypes.lock,
                confirmTitle: `Confirm ${apiFirm.IsLocked ? 'Unlocking' : 'Locking'} Connection for ${firm.Name}`,
                confirmBody: `Please confirm ${apiFirm.IsLocked ? 'unlocking' : 'locking'} the connection for ${firm.Name} to ${apiConnection.APIConnectionName}`
            }
        };
        dispatchConfirmationWorkflowAction(validationSuccessfulAction);

        return false;
    };

    const lockApiFirm = async (apiConnectionFirm: ApiConnectionFirmMapping) => {
        const apiFirm = apiConnectionFirm.apiFirm;
        const firm = apiConnectionFirm.firm;
        const apiConnection = apiConnectionFirm.apiConnection;

        try {
            const response = await Worker.getInstance()
                .updateAPIConnectionFirm({
                    APIConnectionFirmId: apiFirm.APIConnectionFirmId,
                    IsLocked: !apiFirm.IsLocked
                });

            if (response.data && response.data.Status === 48) {
                const serverSuccessAction: ConfirmationWorkflowAction = {
                    type: 'SERVER_SUCCESS',
                    workflowConfiguration: {
                        successTitle: `Successfully ${apiFirm.IsLocked ? 'Unlocked' : 'Locked'} Connection for ${firm.Name}`,
                        successBody: `Successfully ${apiFirm.IsLocked ? 'unlocked' : 'locked'} the connection for ${firm.Name} to ${apiConnection.APIConnectionName}.`
                    }
                };
                dispatchConfirmationWorkflowAction(serverSuccessAction);
            } else if (response.data && doesWorkflowRejectCodeExist(response.data.Status)) {
                const serverErrorAction: ConfirmationWorkflowAction = {
                    type: 'SERVER_ERROR',
                    workflowConfiguration: {
                        failureTitle: `Failed to ${apiFirm.IsLocked ? 'Unlock' : 'Lock'} Connection for ${firm.Name}`,
                        failureBody: `Could not ${apiFirm.IsLocked ? 'unlock' : 'lock'} the connection for ${firm.Name} to ${apiConnection.APIConnectionName}.`
                    }
                };
                dispatchConfirmationWorkflowAction(serverErrorAction);
            }
        } catch (error) {
            const serverErrorAction: ConfirmationWorkflowAction = {
                type: 'SERVER_ERROR',
                workflowConfiguration: {
                    failureTitle: `Failed to ${apiFirm.IsLocked ? 'Unlock' : 'Lock'} Connection for ${firm.Name}`,
                    failureBody: `Something went wrong ${apiFirm.IsLocked ? 'unlocking' : 'locking'} the connection for ${firm.Name} to ${apiConnection.APIConnectionName}.`
                }
            };
            dispatchConfirmationWorkflowAction(serverErrorAction);
        }
    };

    React.useEffect(() => {
        //set initial confirmation settings
        const configureConfirmationWorkflowAction: ConfirmationWorkflowAction = {
            type: 'CONFIGURE_WORKFLOW',
            workflowConfiguration: {
                preActionValidationAndConfig: {
                    [ActionTypes.lock]: lockApiFirmValidation,
                    [ActionTypes.unlock]: lockApiFirmValidation
                },
                onConfirm: {
                    [ActionTypes.lock]: lockApiFirm,
                    [ActionTypes.unlock]: lockApiFirm
                },
                useTimeout: true
            }
        };
        dispatchConfirmationWorkflowAction(configureConfirmationWorkflowAction);
    }, []);

    return (
        <ConfirmationWorkflowContext.Provider value={confirmationContext}>
            <Grid
                container
                item
                justifyContent='center'
            >
                <Grid
                    container
                    direction='row'
                    item
                    justifyContent='center'
                    xs={12}
                >
                    <div
                        className='ag-theme-material'
                        id='apiFirm'
                        style={{
                            width: ' 100%',
                            height: 500
                        }}
                    >
                        <AgGridReact
                            animateRows={false}
                            columnDefs={ApiConnectionFirmsColumnDefs}
                            columnMenu='legacy'
                            components={{ actionButtonsCellRenderer: ActionButtonsCellRenderer }}
                            defaultColDef={{
                                resizable: true,
                                filter: true,
                                sortable: true
                            }}
                            enableCellTextSelection={true}
                            getRowId={({ data }) => (data.firm.FirmId)}
                            onGridReady={onGridReady}
                            rowClassRules={{ 'lockedRow': (params) => !!highlightLockedRow(params, 'apiFirm.IsLocked') }}
                            rowData={firmsFromApiConnection}
                            suppressCellFocus={true}
                            suppressContextMenu={true}
                        />
                    </div>
                </Grid>
            </Grid>
            <ConfirmationWorkflowConsumer />
        </ConfirmationWorkflowContext.Provider>
    );
}